import { motion, AnimatePresence } from "framer-motion";
import { X, Translate, Thermometer, Wind, Gauge, Clock, Heartbeat, ArrowCounterClockwise, EnvelopeSimple, ArrowSquareOut, MoonStars, BellRinging } from "@phosphor-icons/react";
import { LANGUAGES, useSettings } from "@/lib/settings";
import { useQuietHours } from "@/lib/quietHours";
import AlertCategoriesRow from "@/components/v2/AlertCategoriesRow";

const FEEDBACK_EMAIL = "nowcast24@myyahoo.com";
const FEEDBACK_SUBJECT = "Nowcast feedback";
const FEEDBACK_MAILTO = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(FEEDBACK_SUBJECT)}`;

function fmtTime(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function parseTime(value) {
  const [h, m] = (value || "00:00").split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/* =========================================================================
 * SettingsSheet — bottom-drawer modal exposing user preferences.
 *
 * Sections:
 *   • Language (16 options including all major Indian regional languages)
 *   • Temperature  (°C / °F)
 *   • Wind         (km/h / mph / m/s / kt)
 *   • Pressure     (hPa / inHg)
 *   • Time         (12h / 24h)
 *   • Haptics      (on / off)
 *   • Reset to detected defaults
 *
 * Driven by `useSettings()` from `lib/settings.js` — every section is a
 * controlled segmented chooser that calls `update({ key: value })`.
 * Phase 42 (Feb 18 2026).
 * ===================================================================== */

const NUM = {
  fontFamily: "Manrope, system-ui, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.02em",
};

function Row({ icon: Icon, title, sub, children, theme }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="duotone" style={{ color: theme.accent }} />
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
            style={{ color: theme.textSecondary }}
          >
            {title}
          </div>
          {sub ? (
            <div className="text-[10.5px]" style={{ color: theme.textMuted }}>
              {sub}
            </div>
          ) : null}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Segmented({ value, options, onChange, theme, testId }) {
  return (
    <div
      className="inline-flex flex-wrap rounded-full p-0.5 gap-0.5"
      style={{ background: "#0a0a0a08", border: `1px solid ${theme.surfaceBorder}` }}
      data-testid={testId}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-all"
            style={{
              background: active ? theme.textPrimary : "transparent",
              color: active ? theme.surface : theme.textSecondary,
              minWidth: 44,
            }}
            data-testid={`${testId}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function LanguagePicker({ value, onChange, theme }) {
  return (
    <div
      className="grid grid-cols-2 gap-1.5 max-h-[260px] overflow-y-auto pr-1"
      style={{ scrollbarWidth: "thin" }}
      data-testid="settings-language-grid"
    >
      {LANGUAGES.map((lang) => {
        const active = lang.code === value;
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => onChange(lang.code)}
            className="text-left rounded-xl px-3 py-2 transition-all"
            style={{
              background: active ? theme.textPrimary : theme.surface,
              border: `1px solid ${active ? theme.textPrimary : theme.surfaceBorder}`,
              color: active ? theme.surface : theme.textPrimary,
            }}
            data-testid={`settings-lang-${lang.code}`}
          >
            <div
              className="text-[14px] font-extrabold leading-tight"
              style={{ ...NUM, color: active ? theme.surface : theme.textPrimary }}
            >
              {lang.native}
            </div>
            <div
              className="text-[10px] mt-0.5"
              style={{ color: active ? `${theme.surface}cc` : theme.textMuted }}
            >
              {lang.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function SettingsSheet({ open, onClose, theme }) {
  const { settings, update, reset } = useSettings();
  const { prefs: quiet, update: updateQuiet } = useQuietHours();

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40"
            style={{ background: "rgba(20,12,4,0.42)", backdropFilter: "blur(4px)" }}
            onClick={onClose}
            data-testid="settings-scrim"
          />
          {/* Sheet */}
          <motion.div
            key="sheet"
            role="dialog"
            aria-label="Settings"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed left-0 right-0 bottom-0 z-50 max-h-[88dvh] flex flex-col rounded-t-[24px]"
            style={{
              background: theme.surface,
              borderTop: `1px solid ${theme.surfaceBorder}`,
              boxShadow: "0 -22px 60px -32px rgba(20,12,4,0.45)",
            }}
            data-testid="settings-sheet"
          >
            {/* Drag handle */}
            <div className="pt-3 pb-1 flex items-center justify-center">
              <div
                className="h-1 w-10 rounded-full"
                style={{ background: theme.surfaceBorder }}
                aria-hidden
              />
            </div>
            {/* Header */}
            <div className="px-5 pb-3 flex items-end justify-between">
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.32em] font-extrabold"
                  style={{ color: theme.textMuted }}
                >
                  Preferences
                </div>
                <h2
                  className="text-[22px] font-extrabold tracking-tight leading-tight mt-0.5"
                  style={{ ...NUM, color: theme.textPrimary }}
                >
                  Settings
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full transition-colors"
                style={{ background: "#0a0a0a08", border: `1px solid ${theme.surfaceBorder}` }}
                aria-label="Close settings"
                data-testid="settings-close"
              >
                <X size={16} weight="bold" style={{ color: theme.textPrimary }} />
              </button>
            </div>
            {/* Body */}
            <div
              className="flex-1 overflow-y-auto px-4 pb-6 space-y-3"
              style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
            >
              <Row
                icon={Translate}
                title="Language"
                sub="Affects place search results and AI weather summaries"
                theme={theme}
              >
                <LanguagePicker
                  value={settings.language}
                  onChange={(v) => update({ language: v })}
                  theme={theme}
                />
              </Row>

              <Row icon={Thermometer} title="Temperature" theme={theme}>
                <Segmented
                  value={settings.tempUnit}
                  options={[
                    { value: "C", label: "°C" },
                    { value: "F", label: "°F" },
                  ]}
                  onChange={(v) => update({ tempUnit: v })}
                  theme={theme}
                  testId="settings-temp"
                />
              </Row>

              <Row icon={Wind} title="Wind speed" theme={theme}>
                <Segmented
                  value={settings.windUnit}
                  options={[
                    { value: "kmh", label: "km/h" },
                    { value: "mph", label: "mph" },
                    { value: "ms", label: "m/s" },
                    { value: "kt", label: "kt" },
                  ]}
                  onChange={(v) => update({ windUnit: v })}
                  theme={theme}
                  testId="settings-wind"
                />
              </Row>

              <Row icon={Gauge} title="Pressure" theme={theme}>
                <Segmented
                  value={settings.pressureUnit}
                  options={[
                    { value: "hPa", label: "hPa" },
                    { value: "inHg", label: "inHg" },
                  ]}
                  onChange={(v) => update({ pressureUnit: v })}
                  theme={theme}
                  testId="settings-pressure"
                />
              </Row>

              <Row icon={Clock} title="Time format" theme={theme}>
                <Segmented
                  value={settings.timeFormat}
                  options={[
                    { value: "12h", label: "12 h" },
                    { value: "24h", label: "24 h" },
                  ]}
                  onChange={(v) => update({ timeFormat: v })}
                  theme={theme}
                  testId="settings-time"
                />
              </Row>

              <Row
                icon={Heartbeat}
                title="Haptic feedback"
                sub="Subtle vibrations on swipe + tap (mobile only)"
                theme={theme}
              >
                <Segmented
                  value={settings.haptics ? "on" : "off"}
                  options={[
                    { value: "on", label: "On" },
                    { value: "off", label: "Off" },
                  ]}
                  onChange={(v) => update({ haptics: v === "on" })}
                  theme={theme}
                  testId="settings-haptics"
                />
              </Row>

              <Row
                icon={MoonStars}
                title="Quiet hours"
                sub="Mute storm-watch push notifications during a chosen window. Pro feature."
                theme={theme}
              >
                <div data-testid="settings-quiet-hours">
                  <Segmented
                    value={quiet.quiet_hours_enabled ? "on" : "off"}
                    options={[
                      { value: "on", label: "On" },
                      { value: "off", label: "Off" },
                    ]}
                    onChange={(v) => updateQuiet({ quiet_hours_enabled: v === "on" })}
                    theme={theme}
                    testId="settings-quiet-toggle"
                  />
                  {quiet.quiet_hours_enabled && (
                    <div className="mt-3 grid grid-cols-2 gap-2" data-testid="settings-quiet-times">
                      <label className="flex flex-col gap-1">
                        <span
                          className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
                          style={{ color: theme.textMuted }}
                        >
                          Mute from
                        </span>
                        <input
                          type="time"
                          value={fmtTime(quiet.quiet_start_min)}
                          onChange={(e) => {
                            const v = parseTime(e.target.value);
                            if (v != null) updateQuiet({ quiet_start_min: v });
                          }}
                          data-testid="settings-quiet-start"
                          className="px-2.5 py-1.5 rounded-xl text-[13px] font-semibold"
                          style={{
                            background: theme.id === "night" || theme.id === "storm"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(15,23,42,0.04)",
                            border: `1px solid ${theme.surfaceBorder}`,
                            color: theme.textPrimary,
                            colorScheme: theme.id === "night" || theme.id === "storm" ? "dark" : "light",
                          }}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span
                          className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
                          style={{ color: theme.textMuted }}
                        >
                          Until
                        </span>
                        <input
                          type="time"
                          value={fmtTime(quiet.quiet_end_min)}
                          onChange={(e) => {
                            const v = parseTime(e.target.value);
                            if (v != null) updateQuiet({ quiet_end_min: v });
                          }}
                          data-testid="settings-quiet-end"
                          className="px-2.5 py-1.5 rounded-xl text-[13px] font-semibold"
                          style={{
                            background: theme.id === "night" || theme.id === "storm"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(15,23,42,0.04)",
                            border: `1px solid ${theme.surfaceBorder}`,
                            color: theme.textPrimary,
                            colorScheme: theme.id === "night" || theme.id === "storm" ? "dark" : "light",
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </Row>

              <Row
                icon={BellRinging}
                title="Alert types"
                sub="Pick which Pro push notifications you want to receive."
                theme={theme}
              >
                <AlertCategoriesRow theme={theme} />
              </Row>

              <button
                type="button"
                onClick={reset}
                className="w-full mt-1 rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 transition-colors"
                style={{
                  background: "transparent",
                  border: `1px solid ${theme.surfaceBorder}`,
                  color: theme.textMuted,
                }}
                data-testid="settings-reset"
              >
                <ArrowCounterClockwise size={14} weight="bold" />
                <span className="text-[11px] font-bold uppercase tracking-wider">Reset to defaults</span>
              </button>

              {/* Feedback / Complaints / Suggestions — opens the user's mail
                  client preloaded with our support inbox + subject. */}
              <a
                href={FEEDBACK_MAILTO}
                data-testid="settings-feedback"
                className="block mt-2 rounded-2xl px-4 py-3"
                style={{
                  background: theme.surface,
                  border: `1px solid ${theme.surfaceBorder}`,
                  textDecoration: "none",
                }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      background: "rgba(245,158,11,0.16)",
                      color: "#b45309",
                    }}
                  >
                    <EnvelopeSimple size={15} weight="duotone" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
                      style={{ color: theme.textSecondary }}
                    >
                      Feedback · Complaints · Suggestions
                    </div>
                    <div
                      className="text-[12px] font-semibold truncate mt-0.5"
                      style={{ color: theme.textPrimary }}
                    >
                      {FEEDBACK_EMAIL}
                    </div>
                    <div
                      className="text-[10.5px] mt-0.5"
                      style={{ color: theme.textMuted }}
                    >
                      We read every message — usually reply within 48 h.
                    </div>
                  </div>
                  <ArrowSquareOut
                    size={14}
                    weight="bold"
                    style={{ color: theme.textMuted, flexShrink: 0 }}
                  />
                </div>
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
